import { useSyncExternalStore } from 'react';
import type { UserProfile } from '../types';
import { PLAN_SCHRIFT, planSchriftPt, type SigeProject } from '../components/sige-plan/types';
import { fetchCompanyRows, pickCompanySettingsBlock, type CompanyConfigRow, type CompanySelf } from './companyInvoiceConfig';
import type { Speicher } from './verortung/sitzung';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * PLOTTER-VORLAGEN DES SiGe-PLANS — firmenweit (Block `sigePlanVorlagen`)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Bis Oktober 2026 standen die Plotterbreiten (610 „Standard", 914, 1067,
 * A3, A4) und die Grundschrift (10 pt) fest im Code. Jetzt pflegt sie ein
 * Admin in den globalen Einstellungen (AdminGlobalSettings), und sie gelten
 * fuer die ganze Firma:
 *
 *  - `breiten`           Auswahlliste im Stempelfeld, in der Reihenfolge des
 *                        Admins, optional mit Bezeichnung („A3").
 *  - `standardBreite`    Breite neuer Plaene und jedes Plans ohne eigene
 *                        Breite (`plot_breite_mm` leer).
 *  - `standardSchriftPt` Grundschrift jedes Plans ohne eigenen Wert
 *                        (`darstellung.schriftPt` fehlt).
 *
 * Gespeichert wird der Block wie Rechnungs- und Kategorienblock im Profil des
 * Admins (profiles.settings) — kein SQL. Gelesen wird er nach dem Login fuer
 * jeden Nutzer der Firma: Firmenblock eines Admins (fetchCompanyRows +
 * pickCompanySettingsBlock) vor dem eigenen Block; ist die Abfrage nicht
 * moeglich (offline), gilt der eigene Block, sonst der localStorage-Spiegel
 * des letzten erfolgreichen Ladens (Muster mangelKatalog/laden.ts).
 *
 * Bestehende Plaene behalten ihre gespeicherte Breite und Schrift. Der Druck
 * bekommt die Vorgaben ueber `projektFuerDruck`, ohne dass etwas gespeichert
 * wird. Die Schriftvorgabe landet nie im Plan (der Schluessel entfaellt, siehe
 * mitPlanSchrift).
 *
 * Die BREITE dagegen schreibt der Client nie als NULL: ob die Spalte
 * `sige_projekte.plot_breite_mm` NULL zulaesst, ist nicht belegt (keine DDL im
 * Repo, PROD nicht abfragbar) — ist sie NOT NULL, scheiterte jedes Speichern
 * mit 23502. Deshalb steht bei jedem Schreibweg eine Zahl: neue Plaene
 * (`vorbelegungNeuerPlan`), Plankopf mit leerem Breitenfeld und Versionskopie
 * eines Plans ohne Breite (`plotBreiteZumSpeichern`). Gelesen wird tolerant:
 * ein vorhandenes NULL (Altbestand) gilt als Standardbreite.
 */

// ─── Form und Werkseinstellung ──────────────────────────────────────────────

export interface PlotBreiteVorlage {
    /** Papierbreite in mm, ganzzahlig, PLOT_BREITE.min–max. */
    mm: number;
    /** Optionaler Zusatz in der Auswahl, z. B. „A3" oder „Rolle 36″". */
    bezeichnung?: string;
}

export interface SigePlanVorlagen {
    readonly breiten: readonly PlotBreiteVorlage[];
    readonly standardBreite: number;
    readonly standardSchriftPt: number;
}

/**
 * Erlaubte Breiten. Untergrenze wie MIN_PLOT_W im Generator: darunter liefe
 * die Zeitachse ins Negative (generatorSigePlan.ts). Obergrenze = das freie
 * Feld im Stempelfeld.
 */
export const PLOT_BREITE = { min: 300, max: 1500 } as const;

/** Mehr Einträge machen die Auswahl unübersichtlich, ohne etwas zu gewinnen. */
export const MAX_PLOT_BREITEN = 12;

/** Länge einer Bezeichnung — sie steht in einer Auswahlzeile. */
export const MAX_BEZEICHNUNG = 40;

/**
 * Werkseinstellung = der bisher fest eingebaute Stand. Die frühere Option
 * „297 mm (A4)" entfällt: der Generator klemmt jede Breite auf mindestens
 * 300 mm, ein A4-Blatt ist damit nie entstanden. Ein Plan, der noch 297
 * trägt, behält sie und bekommt sie im Stempelfeld weiter angeboten
 * (plotBreitenOptionen).
 */
export const STANDARD_SIGE_PLAN_VORLAGEN: SigePlanVorlagen = Object.freeze({
    breiten: Object.freeze([
        Object.freeze({ mm: 610 }),
        Object.freeze({ mm: 914 }),
        Object.freeze({ mm: 1067 }),
        Object.freeze({ mm: 420, bezeichnung: 'A3' }),
    ]),
    standardBreite: 610,
    standardSchriftPt: PLAN_SCHRIFT.vorgabe,
});

// ─── Normalisierung (wirft nie) ─────────────────────────────────────────────

const istObjekt = (v: unknown): v is Record<string, unknown> =>
    !!v && typeof v === 'object' && !Array.isArray(v);

const feld = (o: unknown, k: string): unknown => (istObjekt(o) ? o[k] : undefined);

/** Zahl aus Zahl oder Ziffernfolge; alles andere → NaN. */
const alsZahl = (v: unknown): number => {
    if (typeof v === 'number') return v;
    if (typeof v === 'string' && v.trim() !== '') return Number(v.trim().replace(',', '.'));
    return NaN;
};

/** Ganzzahlige Breite im erlaubten Bereich, sonst null. */
export const gueltigePlotBreite = (v: unknown): number | null => {
    const n = alsZahl(v);
    if (!Number.isFinite(n)) return null;
    const mm = Math.round(n);
    return mm >= PLOT_BREITE.min && mm <= PLOT_BREITE.max ? mm : null;
};

/** Grundschrift in den Reglergrenzen, auf PLAN_SCHRIFT.schritt gerundet; Unlesbares → null. */
export const gueltigeSchriftPt = (v: unknown): number | null => {
    const n = alsZahl(v);
    if (!Number.isFinite(n) || n <= 0) return null;
    const geklemmt = Math.min(PLAN_SCHRIFT.max, Math.max(PLAN_SCHRIFT.min, n));
    return Math.round(geklemmt / PLAN_SCHRIFT.schritt) * PLAN_SCHRIFT.schritt;
};

const bezeichnungAus = (v: unknown): string | undefined => {
    if (typeof v !== 'string') return undefined;
    const t = v.replace(/\s+/g, ' ').trim().slice(0, MAX_BEZEICHNUNG).trim();
    return t || undefined;
};

const breitenKopie = (breiten: readonly PlotBreiteVorlage[]): PlotBreiteVorlage[] =>
    breiten.map((b) => (b.bezeichnung ? { mm: b.mm, bezeichnung: b.bezeichnung } : { mm: b.mm }));

const kopie = (v: SigePlanVorlagen): SigePlanVorlagen => ({
    breiten: breitenKopie(v.breiten),
    standardBreite: v.standardBreite,
    standardSchriftPt: v.standardSchriftPt,
});

/**
 * Liste der Breiten: Reihenfolge des Admins, ungültige Einträge (außerhalb
 * PLOT_BREITE, keine Zahl) fallen weg, eine doppelte Breite zählt beim ersten
 * Vorkommen, höchstens MAX_PLOT_BREITEN. Keine Liste oder keine einzige
 * gültige Breite → Werkseinstellung: eine leere Auswahl wäre kein Zustand,
 * den die Oberfläche je erzeugt.
 */
const breitenAus = (roh: unknown): PlotBreiteVorlage[] => {
    if (!Array.isArray(roh)) return breitenKopie(STANDARD_SIGE_PLAN_VORLAGEN.breiten);
    const aus: PlotBreiteVorlage[] = [];
    const gesehen = new Set<number>();
    for (const eintrag of roh) {
        if (aus.length >= MAX_PLOT_BREITEN) break;
        const mm = gueltigePlotBreite(istObjekt(eintrag) ? eintrag.mm : eintrag);
        if (mm === null || gesehen.has(mm)) continue;
        gesehen.add(mm);
        const bezeichnung = bezeichnungAus(feld(eintrag, 'bezeichnung'));
        aus.push(bezeichnung ? { mm, bezeichnung } : { mm });
    }
    return aus.length ? aus : breitenKopie(STANDARD_SIGE_PLAN_VORLAGEN.breiten);
};

/**
 * Bringt einen beliebigen Wert in die Form `SigePlanVorlagen`. Wirft nie;
 * was fehlt oder unlesbar ist, nimmt die Werkseinstellung. Die
 * Standardbreite muss nicht in der Liste stehen — die Auswahl bietet sie
 * dann zusätzlich an (plotBreitenOptionen).
 */
export function normalisiereSigePlanVorlagen(roh: unknown): SigePlanVorlagen {
    try {
        const o = istObjekt(roh) ? roh : {};
        return {
            breiten: breitenAus(o.breiten),
            standardBreite: gueltigePlotBreite(o.standardBreite) ?? STANDARD_SIGE_PLAN_VORLAGEN.standardBreite,
            standardSchriftPt: gueltigeSchriftPt(o.standardSchriftPt) ?? STANDARD_SIGE_PLAN_VORLAGEN.standardSchriftPt,
        };
    } catch {
        // Ein Objekt mit werfendem Getter o. ä. — nie die Anmeldung aufhalten.
        return kopie(STANDARD_SIGE_PLAN_VORLAGEN);
    }
}

const SCHLUESSEL = ['breiten', 'standardBreite', 'standardSchriftPt'] as const;

/**
 * Block aus einem Profil-JSON, wenn er dort GEPFLEGT ist, sonst undefined.
 * Ein leeres Objekt oder ein Fremdwert ist keine Einstellung — sonst gewänne
 * ein nie befüllter Block gegen den gepflegten eines anderen Admins (dieselbe
 * Regel wie hasConfig in companyInvoiceConfig.ts).
 */
export const sigePlanVorlagenBlockAus = (roh: unknown): SigePlanVorlagen | undefined => {
    try {
        if (!istObjekt(roh)) return undefined;
        if (!SCHLUESSEL.some((k) => roh[k] !== undefined && roh[k] !== null)) return undefined;
        return normalisiereSigePlanVorlagen(roh);
    } catch {
        return undefined;
    }
};

/** Der EIGENE Block eines Profils (für den Einstellungsdialog), sonst undefined. */
export const eigenerSigePlanVorlagenBlock = (profil: UserProfile | null | undefined): SigePlanVorlagen | undefined =>
    sigePlanVorlagenBlockAus(feld(profil?.settings, 'sigePlanVorlagen'));

/** Gleicher Inhalt? Die Normalform hat eine feste Schlüsselreihenfolge. */
export const gleicheSigePlanVorlagen = (a: SigePlanVorlagen, b: SigePlanVorlagen): boolean => {
    if (a === b) return true;
    try {
        return JSON.stringify(kopie(a)) === JSON.stringify(kopie(b));
    } catch {
        return false;
    }
};

export const istWerkseinstellung = (v: SigePlanVorlagen): boolean =>
    gleicheSigePlanVorlagen(v, STANDARD_SIGE_PLAN_VORLAGEN);

// ─── Firmenauflösung ────────────────────────────────────────────────────────

export interface GewaehlteSigePlanVorlagen {
    vorlagen: SigePlanVorlagen;
    /** Profil, aus dem der Block stammt. */
    quelleId: string;
}

/** Firmenblock nach der gemeinsamen Regel (eigenes Admin-Profil, dann super_admin vor admin, dann id). */
export const pickCompanySigePlanVorlagen = (
    rows: CompanyConfigRow[],
    self: CompanySelf,
): GewaehlteSigePlanVorlagen | undefined =>
    pickCompanySettingsBlock(rows, self, (r) => {
        const vorlagen = sigePlanVorlagenBlockAus(feld(r.settings, 'sigePlanVorlagen'));
        return vorlagen ? { vorlagen, quelleId: r.id } : undefined;
    });

// ─── Laufzeit-Stand (Modul-Store) ───────────────────────────────────────────

export interface SigePlanVorlagenStand {
    vorlagen: SigePlanVorlagen;
    /** Profil, dessen Block gilt; null = Werkseinstellung. */
    quelleId: string | null;
}

const GRUNDSTAND: SigePlanVorlagenStand = Object.freeze({ vorlagen: STANDARD_SIGE_PLAN_VORLAGEN, quelleId: null });

let stand: SigePlanVorlagenStand = GRUNDSTAND;
const zuhoerer = new Set<() => void>();
/**
 * Zählt jede wirksame Änderung. Der Lader übernimmt sein Ergebnis nur, wenn
 * in der Zwischenzeit niemand (der Einstellungsdialog) etwas gesetzt hat —
 * sonst überschriebe eine langsame Abfrage die frische Eingabe des Admins.
 */
let aenderungen = 0;

function melden(): void {
    for (const f of [...zuhoerer]) {
        try {
            f();
        } catch {
            // Ein Zuhörer darf die anderen nicht aufhalten.
        }
    }
}

export function sigePlanVorlagenAbonnieren(f: () => void): () => void {
    zuhoerer.add(f);
    return () => {
        zuhoerer.delete(f);
    };
}

/** Wirksamer Stand mit Herkunft (dieselbe Referenz, solange sich nichts ändert). */
export const sigePlanVorlagenStand = (): SigePlanVorlagenStand => stand;

/** Wirksame Vorlagen — für Stellen außerhalb von React (Druck, Anlage). */
export const sigePlanVorlagenJetzt = (): SigePlanVorlagen => stand.vorlagen;

/**
 * Setzt die wirksamen Vorlagen (werden normalisiert). `null` = Werkseinstellung.
 * Gleicher Inhalt und gleiche Herkunft → keine Meldung.
 */
export function setzeSigePlanVorlagen(roh: unknown, quelleId: string | null): void {
    const vorlagen = roh == null ? STANDARD_SIGE_PLAN_VORLAGEN : normalisiereSigePlanVorlagen(roh);
    const quelle = roh == null ? null : typeof quelleId === 'string' && quelleId ? quelleId : null;
    const inhaltGleich = gleicheSigePlanVorlagen(stand.vorlagen, vorlagen);
    if (inhaltGleich && stand.quelleId === quelle) return;
    aenderungen++;
    stand = { vorlagen: inhaltGleich ? stand.vorlagen : vorlagen, quelleId: quelle };
    melden();
}

export function useSigePlanVorlagen(): SigePlanVorlagen {
    return useSyncExternalStore(sigePlanVorlagenAbonnieren, sigePlanVorlagenJetzt, sigePlanVorlagenJetzt);
}

export function useSigePlanVorlagenStand(): SigePlanVorlagenStand {
    return useSyncExternalStore(sigePlanVorlagenAbonnieren, sigePlanVorlagenStand, sigePlanVorlagenStand);
}

// ─── Laden nach dem Login ───────────────────────────────────────────────────

export const VORLAGEN_SPIEGEL_SCHLUESSEL = 'sigeko.sigePlanVorlagen.v1';

interface Spiegel {
    nutzerId: string;
    vorlagen: SigePlanVorlagen | null;
    quelleId: string | null;
}

const standardSpeicher = (): Speicher | null => {
    try {
        return typeof localStorage !== 'undefined' ? localStorage : null;
    } catch {
        return null;
    }
};

const text = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

function spiegelLesen(nutzerId: string, speicher: Speicher | null): Spiegel | null {
    if (!speicher) return null;
    try {
        const roh: unknown = JSON.parse(speicher.getItem(VORLAGEN_SPIEGEL_SCHLUESSEL) ?? 'null');
        if (!istObjekt(roh) || roh.nutzerId !== nutzerId) return null;
        return {
            nutzerId,
            vorlagen: sigePlanVorlagenBlockAus(roh.vorlagen) ?? null,
            quelleId: text(roh.quelleId) || null,
        };
    } catch {
        return null;
    }
}

function spiegelSchreiben(s: Spiegel, speicher: Speicher | null): void {
    if (!speicher) return;
    try {
        if (!s.vorlagen) speicher.removeItem(VORLAGEN_SPIEGEL_SCHLUESSEL);
        else speicher.setItem(VORLAGEN_SPIEGEL_SCHLUESSEL, JSON.stringify(s));
    } catch {
        // Nur Rückfall für offline.
    }
}

/** Hochgezählt bei jedem Laden/Leeren: ein überholter Lauf setzt nichts mehr. */
let generation = 0;

/**
 * Ladezustand — für die ANLAGE neuer Pläne (sigePlanVorlagenFuerAnlage), die
 * die Standardbreite dauerhaft in den Plan schreibt:
 *  - 'offen'          noch nie geladen (vor dem Login, nach dem Abmelden)
 *  - 'laedt'          Lader läuft; `ladevorgang` ist sein Promise
 *  - 'fertig'         Firmenblock bestimmt (oder ohne Firma: eigener Block /
 *                     Werkseinstellung — das ist dann der richtige Stand)
 *  - 'fehlgeschlagen' Abfrage gescheitert; es gilt eigener Block, Spiegel oder
 *                     Werkseinstellung — nicht gesichert
 */
type Ladestatus = 'offen' | 'laedt' | 'fertig' | 'fehlgeschlagen';
let ladestatus: Ladestatus = 'offen';
let ladevorgang: Promise<void> | null = null;
/** Nutzer des laufenden/letzten Ladens — für den Spiegel nach Ablauf der Zeitgrenze. */
let ladeNutzerId: string | null = null;
/**
 * Zählt Sitzungswechsel (Abmelden, Laden für einen anderen Nutzer). Eine
 * wartende Anlage prüft ihn nach dem Warten (sigePlanVorlagenFuerAnlage).
 * Nicht `generation`: die steigt auch beim Neuladen desselben Nutzers.
 */
let sitzung = 0;

async function ladeIntern(profil: UserProfile | null | undefined, meine: number): Promise<void> {
    const aenderungenBeimStart = aenderungen;
    let ergebnis: Ladestatus = 'fertig';
    try {
        const nutzerId = text(profil?.id) || null;
        const speicher = standardSpeicher();
        const eigene = eigenerSigePlanVorlagenBlock(profil);
        let gewaehlt: GewaehlteSigePlanVorlagen | null = eigene && nutzerId ? { vorlagen: eigene, quelleId: nutzerId } : null;

        const self: CompanySelf = { id: profil?.id, company_name: profil?.company_name, company_id: profil?.company_id };
        const mitFirma = !!text(profil?.company_name) || !!text(profil?.company_id);
        let firmaGeladen = false;
        if (mitFirma) {
            try {
                const firma = pickCompanySigePlanVorlagen(await fetchCompanyRows(), self);
                firmaGeladen = true;
                if (firma) gewaehlt = firma;
            } catch (e) {
                ergebnis = 'fehlgeschlagen';
                console.warn('Firmen-Vorlagen des SiGe-Plans nicht ladbar', e);
            }
        }
        if (meine !== generation) return;
        // Hat der Admin inzwischen selbst etwas gesetzt, gilt seine Eingabe —
        // sie ist dann auch der gesicherte Stand für eine Anlage.
        if (aenderungen !== aenderungenBeimStart) {
            ergebnis = 'fertig';
            return;
        }

        if (firmaGeladen && nutzerId) {
            spiegelSchreiben({ nutzerId, vorlagen: gewaehlt?.vorlagen ?? null, quelleId: gewaehlt?.quelleId ?? null }, speicher);
        } else if (mitFirma && !gewaehlt && nutzerId) {
            // Abfrage fehlgeschlagen und kein eigener Block: letzter bekannter Stand.
            const spiegel = spiegelLesen(nutzerId, speicher);
            if (spiegel?.vorlagen) gewaehlt = { vorlagen: spiegel.vorlagen, quelleId: spiegel.quelleId ?? '' };
        }
        setzeSigePlanVorlagen(gewaehlt?.vorlagen ?? null, gewaehlt?.quelleId || null);
    } catch (e) {
        ergebnis = 'fehlgeschlagen';
        console.warn('Vorlagen des SiGe-Plans nicht ladbar', e);
    } finally {
        if (meine === generation) ladestatus = ergebnis;
    }
}

/**
 * Lädt die wirksamen Vorlagen der Firma in den Store. Best effort, wirft nie:
 * an einer Druckvorgabe darf die Anmeldung nicht scheitern. Bis das Ergebnis
 * da ist, gilt für Anzeige und Druck die Werkseinstellung (bzw. der bisherige
 * Stand); eine Anlage wartet darauf (sigePlanVorlagenFuerAnlage).
 */
export function ladeSigePlanVorlagen(profil: UserProfile | null | undefined): Promise<void> {
    const meine = ++generation;
    const nutzerId = text(profil?.id) || null;
    if (nutzerId !== ladeNutzerId) sitzung++;
    ladestatus = 'laedt';
    ladeNutzerId = nutzerId;
    const p = ladeIntern(profil, meine);
    ladevorgang = p;
    void p.finally(() => {
        if (ladevorgang === p) ladevorgang = null;
    });
    return p;
}

/**
 * Was eine Anlage bekommt.
 *  - `abgebrochen: false` — die Vorlagen, und ob sie gesichert die der Firma
 *    sind (`geladen: false` = Zeitgrenze oder offline: Spiegel bzw.
 *    Werkseinstellung).
 *  - `abgebrochen: true`  — während des Wartens hat die Sitzung gewechselt
 *    (Abmelden, anderer Nutzer). Dann darf NICHTS angelegt werden: die
 *    Vorlagen gehörten zum neuen Nutzer, der Plan zum alten.
 *
 * Prüfen mit `anlage.abgebrochen === true`: ohne strictNullChecks (tsconfig)
 * engt TypeScript die Union über den bloßen Wahrheitswert nicht ein.
 */
export type SigePlanVorlagenFuerAnlage =
    | { abgebrochen: false; vorlagen: SigePlanVorlagen; geladen: boolean }
    | { abgebrochen: true };

/** Zeitgrenze, wie lange eine Anlage auf den laufenden Ladevorgang wartet. */
export const ANLAGE_ZEITGRENZE_MS = 5000;

/** Meldung der Anlagewege, wenn `abgebrochen` zurückkommt. */
export const ANLAGE_ABGEBROCHEN_TEXT = 'Die Anmeldung hat inzwischen gewechselt — es wurde nichts angelegt.';

const warte = (ms: number, laufend: Promise<void>): Promise<void> => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const abgelaufen = new Promise<void>((r) => {
        timer = setTimeout(r, Math.max(0, ms));
    });
    return Promise.race([laufend, abgelaufen]).finally(() => {
        if (timer !== undefined) clearTimeout(timer);
    });
};

/**
 * Vorlagen für die ANLAGE eines Plans (und jedes andere dauerhafte Schreiben
 * der Standardbreite). Anders als Anzeige und Druck schreibt die Anlage die
 * Breite dauerhaft in den Plan — mitten im Laden nach dem Login bekäme er
 * sonst für immer die Werkseinstellung. Deshalb: läuft der Lader, wird auf
 * ihn gewartet (insgesamt höchstens `zeitgrenzeMs`; startet währenddessen ein
 * neuer Lauf desselben Nutzers, wird auf den gewartet). Danach gilt der
 * Spiegel des letzten erfolgreichen Ladens, sonst die Werkseinstellung — mit
 * `geladen: false`, damit die Oberfläche es sagen kann.
 *
 * Sitzung und Nutzer werden beim Eintritt festgehalten und nach dem Warten
 * geprüft: hat inzwischen jemand anderes übernommen, kommt `abgebrochen`
 * (Codex-Review — sonst legte die wartende Anlage von A mit den Vorlagen von
 * B an). Wirft nie.
 */
export async function sigePlanVorlagenFuerAnlage(
    zeitgrenzeMs: number = ANLAGE_ZEITGRENZE_MS,
): Promise<SigePlanVorlagenFuerAnlage> {
    const meineSitzung = sitzung;
    const meinNutzer = ladeNutzerId;
    const gewechselt = () => sitzung !== meineSitzung || ladeNutzerId !== meinNutzer;
    try {
        const frist = Date.now() + Math.max(0, zeitgrenzeMs);
        while (ladestatus === 'laedt' && ladevorgang && !gewechselt()) {
            const rest = frist - Date.now();
            if (rest <= 0) break;
            await warte(rest, ladevorgang);
        }
        if (gewechselt()) return { abgebrochen: true };
        if (ladestatus === 'fertig') return { abgebrochen: false, vorlagen: stand.vorlagen, geladen: true };
        if (ladestatus === 'laedt') {
            // Zeitgrenze: letzter bekannter Stand dieses Nutzers, sonst Werkseinstellung.
            const spiegel = meinNutzer ? spiegelLesen(meinNutzer, standardSpeicher()) : null;
            return { abgebrochen: false, vorlagen: spiegel?.vorlagen ?? STANDARD_SIGE_PLAN_VORLAGEN, geladen: false };
        }
        // 'fehlgeschlagen' (Store trägt schon eigenen Block, Spiegel oder
        // Werkseinstellung) oder 'offen' (nie geladen).
        return { abgebrochen: false, vorlagen: stand.vorlagen, geladen: false };
    } catch {
        return gewechselt() ? { abgebrochen: true } : { abgebrochen: false, vorlagen: stand.vorlagen, geladen: false };
    }
}

/**
 * Nach „Auf Werkseinstellung zurücksetzen" im Einstellungsdialog (eigener
 * Block gelöscht und gespeichert): den wirksamen Firmenblock neu bestimmen —
 * gilt der eines anderen Admins, soll dieser Client ihn sofort zeigen. Der
 * Spiegel trägt noch den gelöschten Block und wird vorher verworfen.
 */
export async function ladeSigePlanVorlagenOhneEigenenBlock(profil: UserProfile | null | undefined): Promise<void> {
    try {
        standardSpeicher()?.removeItem(VORLAGEN_SPIEGEL_SCHLUESSEL);
    } catch {
        // Nur Rückfall für offline.
    }
    if (!profil) return;
    const settings: Record<string, unknown> = { ...(profil.settings ?? {}) };
    delete settings.sigePlanVorlagen;
    await ladeSigePlanVorlagen({ ...profil, settings } as UserProfile);
}

/** Beim Abmelden: der nächste Nutzer im selben Tab erbt nichts. */
export function leereSigePlanVorlagen(): void {
    generation++;
    sitzung++;
    ladestatus = 'offen';
    ladevorgang = null;
    ladeNutzerId = null;
    if (stand === GRUNDSTAND) return;
    aenderungen++;
    stand = GRUNDSTAND;
    melden();
}

// ─── Anwendung am Plan ──────────────────────────────────────────────────────

/**
 * Gespeicherte Breite eines Plans, null = keine eigene Einstellung. Nimmt
 * auch eine Ziffernfolge an: die Auswahlliste lieferte früher den Wert als
 * Text, und so steht er nach dem Speichern bis zum Neuladen im Zustand.
 */
export const gespeichertePlotBreite = (wert: unknown): number | null => {
    const n = alsZahl(wert);
    return Number.isFinite(n) && n > 0 ? n : null;
};

/** Breite, mit der der Plan gedruckt wird. */
export const wirksamePlotBreite = (project: Pick<SigeProject, 'plot_breite_mm'>, vorlagen: SigePlanVorlagen): number =>
    gespeichertePlotBreite(project.plot_breite_mm) ?? vorlagen.standardBreite;

/**
 * Der Plan, wie ihn der Generator bekommt: leere Breite → Standardbreite der
 * Firma, fehlende Schrift → Firmenvorgabe. NUR für den Druck — das Ergebnis
 * darf nie gespeichert werden, sonst fröre es die heutige Vorgabe im Plan ein
 * und eine spätere Änderung der Firma erreichte ihn nicht mehr.
 */
export const projektFuerDruck = (project: SigeProject, vorlagen: SigePlanVorlagen): SigeProject => ({
    ...project,
    plot_breite_mm: wirksamePlotBreite(project, vorlagen),
    darstellung: {
        ...(project.darstellung || {}),
        schriftPt: planSchriftPt(project.darstellung, vorlagen.standardSchriftPt),
    },
});

/**
 * Felder, mit denen ein NEUER Plan angelegt wird. Die Breite wird hier
 * gespeichert (anders als beim Druck): ein angelegter Plan soll seine Breite
 * behalten, auch wenn die Firma die Standardbreite später ändert.
 */
export const vorbelegungNeuerPlan = (vorlagen: SigePlanVorlagen): { plot_breite_mm: number } => ({
    plot_breite_mm: vorlagen.standardBreite,
});

/**
 * Breite, die in die Datenbank geht — NIE null (siehe Kopf: ob die Spalte
 * NULL zulässt, ist nicht belegt). Ein leeres oder unlesbares Feld wird zur
 * aktuellen Standardbreite der Firma; eine eigene Breite bleibt, wie sie ist
 * (auch eine alte 297, die der Generator ohnehin auf 300 klemmt).
 */
export const plotBreiteZumSpeichern = (wert: unknown, vorlagen: SigePlanVorlagen): number =>
    gespeichertePlotBreite(wert) ?? vorlagen.standardBreite;

/**
 * Schreibdaten mit gesicherter Breite: `plot_breite_mm` wird durch
 * `plotBreiteZumSpeichern` ersetzt, alles andere bleibt unverändert. Für den
 * Plankopf (Stempelfeld) und die Versionskopie — jeder Weg, der eine ganze
 * Zeile oder das Breitenfeld schreibt.
 */
export const mitPlotBreiteZumSpeichern = <T extends { plot_breite_mm?: unknown }>(
    daten: T,
    vorlagen: SigePlanVorlagen,
): Omit<T, 'plot_breite_mm'> & { plot_breite_mm: number } => ({
    ...daten,
    plot_breite_mm: plotBreiteZumSpeichern(daten.plot_breite_mm, vorlagen),
});

// ─── Auswahlliste im Stempelfeld ────────────────────────────────────────────

export interface PlotBreiteOption {
    mm: number;
    label: string;
    /** Die Standardbreite der Firma. */
    standard: boolean;
    /** Nicht in der Vorlage — die Breite dieses Plans oder eine frei eingegebene. */
    eigene: boolean;
}

const mmText = (mm: number): string => mm.toLocaleString('de-DE', { useGrouping: false, maximumFractionDigits: 1 });

const option = (mm: number, bezeichnung: string | undefined, standard: boolean, eigene: boolean): PlotBreiteOption => {
    const zusatz = [bezeichnung, standard ? 'Standard' : '', eigene ? 'eigene Breite' : ''].filter(Boolean).join(', ');
    return { mm, standard, eigene, label: zusatz ? `${mmText(mm)} mm (${zusatz})` : `${mmText(mm)} mm` };
};

/**
 * Optionen der Plotterbreite: die Vorlage in der Reihenfolge des Admins,
 * „Standard" an der Standardbreite. Steht die Standardbreite nicht in der
 * Liste, kommt sie vorne dazu; ist `aktuelle` (die Breite im Formular) keine
 * der Vorlagen, hinten als „eigene Breite" — ein Plan mit z. B. 297 mm soll
 * seine Breite in der Auswahl sehen und nicht stillschweigend auf eine andere
 * springen.
 */
export const plotBreitenOptionen = (vorlagen: SigePlanVorlagen, aktuelle?: unknown): PlotBreiteOption[] => {
    const liste = vorlagen.breiten.map((b) => option(b.mm, b.bezeichnung, b.mm === vorlagen.standardBreite, false));
    if (!liste.some((o) => o.mm === vorlagen.standardBreite)) {
        liste.unshift(option(vorlagen.standardBreite, undefined, true, false));
    }
    const a = gespeichertePlotBreite(aktuelle);
    if (a !== null && !liste.some((o) => o.mm === a)) liste.push(option(a, undefined, false, true));
    return liste;
};

/** „10 pt" / „10,5 pt" für Beschriftungen. */
export const schriftPtText = (pt: number): string =>
    `${pt.toLocaleString('de-DE', { maximumFractionDigits: 1 })} pt`;
